import { useEffect, useState } from "react";
import Table from "../../component/table/Table";
import {
  APICreateContent,
  APIDeleteContent,
  APIGetContent,
  APIUpdateContent,
} from "../../config/utilities/APIs/contentApi";
import { useAuth } from "../../config/utilities/useAuth";
import Title from "../../component/title/Title";
import NavBar from "../../component/navBar/NavBar";
import DeleteModal from "../../component/modal/DeleteModal";
import SnackBar from "../../component/Snackbar/Snackbar";
import { ServiceAnnouncementForm } from "./ServiceAnnouncementForm";
import DeactivationModal from "../../component/modal/DeactivationModal";
import "./ServiceAnnouncement.css";

const headers = [
  { key: "title", name: "Announcement Title", sortable: false },
  { key: "icon", name: "Icon", sortable: false },
  { key: "titleColor", name: "Header Colour", sortable: false },
  { key: "content", name: "Preview Content", sortable: false },
  { key: "status", name: "Status", sortable: false },
];

export default function ServiceAnnouncement() {
  const { privilege, token } = useAuth();
  const role = privilege?.role;
  const institutionId = privilege?.institutionId;
  const [action, setAction] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarType, setSnackbarType] = useState("");
  const [snackbarTitle, setSnackbarTitle] = useState("");
  const [snackbarSubtitle, setSnackbarSubtitle] = useState("");
  const [selectedRowData, setSelectedRowData] = useState(null);
  const [showDeactivationModal, setShowDeactivationModal] = useState(false);
  const [fetchedServiceAnnouncementData, setFetchedServiceAnnouncementData] =
    useState([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [selectedFilters, setSelectedFilters] = useState({
    type: "service-announcement",
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [input, setInput] = useState({
    title: "",
    titleColor: "",
    content: "",
    icon: "",
    status: "active",
  });

  const [error, setError] = useState({
    title: false,
    titleColor: false,
    content: false,
    icon: false,
  });

  const fetchServiceAnnouncement = async (
    page = 1,
    filters = selectedFilters,
    searchTerm,
  ) => {
    setLoading(true);

    try {
      const response = await APIGetContent(
        page,
        filters,
        setLoading,
        institutionId,
        searchTerm,
      );

      if (response.status === 200) {
        const { data } = response.data;
        setFetchedServiceAnnouncementData(data);
        setPageNumber(page);
      } else if (response.status === 400) {
        console.warn("Bad Request:", response);
      } else if (response.status === 401) {
        console.warn("Unauthorized:", response);
      } else if (response.status === 403) {
        console.warn("Forbidden:", response);
      } else if (response.status === 404) {
        console.warn("Not Found:", response);
      } else if (response.status >= 500) {
        console.error("Server Error:", response);
      } else {
        console.warn("Unexpected Status Code:", response.status, response);
      }
    } catch (error) {
      console.error("Error fetching service announcement data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchServiceAnnouncement(1, selectedFilters, searchTerm);
  }, [selectedFilters, searchTerm]);

  const handlePopoverAction = (action, rowId) => {
    const selectedRow = fetchedServiceAnnouncementData.cmsData.find(
      (row) => row._id === rowId,
    );
    if (action === "edit") {
      setSelectedRowData(selectedRow);
      setAction("edit");
    } else if (action === "delete") {
      setSelectedRowData(selectedRow);
      setShowDeleteModal(true);
    } else if (action === "activate") {
      setSelectedRowData(selectedRow);
      setAction("activate");
      setShowDeactivationModal(true);
    } else if (action === "deactivate") {
      setSelectedRowData(selectedRow);
      setAction("deactivate");
      setShowDeactivationModal(true);
    } else {
      setSelectedRowData(selectedRow);
      setAction("view");
    }
  };

  const handleOpenCreate = () => {
    setAction("create");
  };

  const handleCreate = async () => {
    setLoading(true);
    const formattedInput = {
      type: "service-announcement",
      title: input.title,
      titleColor: input.titleColor,
      icon: input.icon,
      content: input.content,
    };
    try {
      const response = await APICreateContent(
        formattedInput,
        setLoading,
        institutionId,
        token,
      );
      if (response?.status === 200) {
        setShowSnackbar(true);
        setSnackbarTitle("Create Successful");
        setSnackbarSubtitle(
          "Service Announcement has been created successfuly",
        );
        setSnackbarType("success");
      } else {
        throw new Error(
          response?.response?.data?.message ||
            "Failed to create service announcement",
        );
      }
    } catch (error) {
      setShowSnackbar(true);
      setSnackbarTitle("Create Failed");
      setSnackbarSubtitle(error.message || "An error occurred");
      setSnackbarType("error");
    } finally {
      handleReset();
    }
  };

  const handleEdit = async () => {
    if (!selectedRowData?._id) return;

    setLoading(true);

    const formattedInput = {
      id: selectedRowData._id,
      type: "service-announcement",
      title: input.title,
      titleColor: input.titleColor,
      icon: input.icon,
      content: input.content,
    };

    try {
      const response = await APIUpdateContent(
        formattedInput,
        setLoading,
        institutionId,
        token,
      );

      if (response?.status === 200) {
        setShowSnackbar(true);
        setSnackbarTitle("Update Successful");
        setSnackbarSubtitle(
          "Service Announcement has been updated successfully",
        );
        setSnackbarType("success");
      } else {
        throw new Error(
          response?.response?.data?.message ||
            "Failed to update service announcement",
        );
      }
    } catch (error) {
      setShowSnackbar(true);
      setSnackbarTitle("Update Failed");
      setSnackbarSubtitle(error.message || "An error occurred");
      setSnackbarType("error");
    } finally {
      handleReset();
    }
  };

  const handleStatus = async (action) => {
    setLoading(true);

    const isActivate = action === "activate";

    const status = isActivate ? "active" : "deactive";
    const title = isActivate ? "Activation" : "Deactivation";

    const payload = {
      status,
      id: selectedRowData._id,
      type: "service-announcement",
    };

    try {
      const response = await APIUpdateContent(
        payload,
        setLoading,
        institutionId,
        token,
      );

      if (response?.status === 200) {
        setShowSnackbar(true);
        setSnackbarTitle(`${title} Successful`);
        setSnackbarSubtitle(
          `Service Announcement has been ${
            isActivate ? "activated" : "deactivated"
          } successfully`,
        );
        setSnackbarType("success");
      }
    } catch (error) {
      setShowSnackbar(true);
      setSnackbarTitle(`${title} Failed`);
      setSnackbarSubtitle(error.message || "An error occurred");
      setSnackbarType("error");
    } finally {
      handleReset();
    }
  };

  const handleReset = async () => {
    setInput({
      title: "",
      titleColor: "",
      content: "",
      icon: "",
      status: "active",
    });
    setError({
      title: false,
      titleColor: false,
      content: false,
      icon: false,
    });
    setFetchedServiceAnnouncementData([]);
    setShowDeleteModal(false);
    setSelectedRowData(null);
    setAction(null);
    setPageNumber(1);
    setShowDeactivationModal(false);
    await fetchServiceAnnouncement(1, selectedFilters, searchTerm);
  };

  const handleDelete = async (row) => {
    setLoading(true);

    const rowId = row._id;

    try {
      const response = await APIDeleteContent(
        rowId,
        setLoading,
        institutionId,
        token,
      );

      if (response?.status === 200) {
        setShowSnackbar(true);
        setSnackbarTitle("Delete Successful");
        setSnackbarSubtitle(
          "Service Announcement has been deleted successfuly",
        );
        setSnackbarType("success");
      } else {
        throw new Error(
          response?.response?.data?.message ||
            "Failed to delete service announcement",
        );
      }
    } catch (error) {
      setShowSnackbar(true);
      setSnackbarTitle("Delete Failed");
      setSnackbarSubtitle(error.message || "An error occurred");
      setSnackbarType("error");
    } finally {
      handleReset();
    }
  };

  const renderTitle = () => {
    switch (action) {
      case "edit":
        return (
          <Title
            title="Edit Service Announcement"
            titleSize="1.5rem"
            gap="0.5rem"
            lineHeight="2.5rem"
          />
        );
      case "create":
        return (
          <Title
            title="Create Service Announcement"
            titleSize="1.5rem"
            gap="0.5rem"
            lineHeight="2.5rem"
          />
        );
      case "view":
        return (
          <Title
            title="View Service Announcement"
            titleSize="1.5rem"
            gap="0.5rem"
            lineHeight="2.5rem"
          />
        );
      default:
        return (
          <Title
            title="Service Announcement"
            subtitle="Set the contents for the service announcement card on the parent app. This card appears in the scheduling module."
            titleSize="1.5rem"
            subtitleSize="1rem"
            gap="0.5rem"
            lineHeight="2rem"
          />
        );
    }
  };

  const renderContent = () => {
    switch (action) {
      case "edit":
        return (
          <ServiceAnnouncementForm
            mode="edit"
            existingData={selectedRowData}
            onSubmit={handleEdit}
            handleReset={handleReset}
            loading={loading}
            setLoading={setLoading}
            input={input}
            setInput={setInput}
            error={error}
            setError={setError}
            role={role}
          />
        );
      case "create":
        return (
          <ServiceAnnouncementForm
            mode="create"
            onSubmit={handleCreate}
            handleReset={handleReset}
            loading={loading}
            setLoading={setLoading}
            input={input}
            setInput={setInput}
            error={error}
            setError={setError}
          />
        );
      case "view":
        return (
          <ServiceAnnouncementForm
            mode="view"
            existingData={selectedRowData}
            handleReset={handleReset}
            loading={loading}
            setLoading={setLoading}
            input={input}
            setInput={setInput}
            error={error}
            setError={setError}
          />
        );
      default:
        return (
          <Table
            data={fetchedServiceAnnouncementData?.cmsData || []}
            searchText="Search by title or content..."
            headers={headers}
            showEmptyHeader={true}
            showFilter={false}
            handlePopoverAction={handlePopoverAction}
            onClick={handleOpenCreate}
            module="serviceAnnouncement"
            loading={loading}
            showTableAction={
              role === "ADMIN" || role === "SUPERADMIN" || role === "EDITOR"
            }
            height="75vh"
            useBackendPagination={true}
            onPageChange={fetchServiceAnnouncement}
            totalPages={
              fetchedServiceAnnouncementData?.pagination?.totalPages || 1
            }
            currentPage={pageNumber}
            totalRecords={
              fetchedServiceAnnouncementData?.pagination?.total || 0
            }
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
          />
        );
    }
  };

  return (
    <div
      className={`main-page service-announcement-page ${
        !action ? "service-announcement-table-view" : ""
      }`}
    >
      <NavBar
        type="serviceAnnouncement"
        action={action}
        handleReset={handleReset}
      />
      <div className="content-area service-announcement-content-area">
        <div className="title-container mobile-hide-page-title">{renderTitle()}</div>
        <div className="main-content-container service-announcement-main-content-container">
          {renderContent()}
        </div>
      </div>
      <DeleteModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onDelete={handleDelete}
        itemName={selectedRowData}
        module="serviceAnnouncement"
        loading={loading}
      />
      <DeactivationModal
        isOpen={showDeactivationModal}
        onClose={() => setShowDeactivationModal(false)}
        loading={loading}
        onToggle={handleStatus}
        itemName={selectedRowData}
        module="serviceAnnouncement"
        action={action}
      />
      {showSnackbar && (
        <div className="notification">
          <SnackBar
            state={showSnackbar}
            setState={setShowSnackbar}
            title={snackbarTitle}
            subtitle={snackbarSubtitle}
            module={snackbarType}
            action={snackbarType}
          />
        </div>
      )}
    </div>
  );
}
